import { useMutation, useQueryClient } from "@tanstack/react-query";
import { pathPickMembershipMutationApi } from "@/features/pathpick/features/membership/api/mutation";
import { PathPickMembershipApplyRequestBody } from "@/features/pathpick/features/membership/types/request-body";
import { PATHPICK_MEMBERSHIP_APPLICATIONS_QUERY_KEY } from "@/features/pathpick/features/membership/constants";
import { BUYER_QUERY_KEY } from "@/shared/api/buyer/query";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import useGetMySelf from "@/features/auth/use-get-my-self";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";

// 패스픽 멤버십 신청.
// 플랜 자체는 어드민이 입금을 확인한 뒤에 바뀌므로 멤버십 캐시는 아직 무효화할 게 없지만,
// 신청 이력(hasPathpickMembershipApplication)은 접수 즉시 true가 되므로 /users 응답만 갱신한다.
// 성공 안내 문구는 업/다운그레이드에 따라 달라서 호출부가 띄운다.
const useApplyPathPickMembership = () => {
  const { companyId } = useCompanyIdentity();
  const queryClient = useQueryClient();
  const { mutate: revalidateMySelf } = useGetMySelf();
  const { error } = useAppToasts({ id: "GLOBAL" });

  return useMutation({
    mutationFn: (body: PathPickMembershipApplyRequestBody) =>
      pathPickMembershipMutationApi.applyPathPickMembership(
        companyId as number,
        body
      ),
    // 첫 달 프로모션 대상 판정이 신청 이력에 걸려 있어, 새로고침 없이 정가 UI로 바뀌게 한다.
    onSuccess: async () => {
      revalidateMySelf();
      // 접수된 신청은 입금 대기 상태라, 내역을 다시 읽어 추가 신청을 막는다.
      // 이 무효화만 기다린다 — POST 성공 응답 바디가 비어 있어 접수된 건을 응답에서 못 얻고,
      // 호출부가 await 직후 요약 모달을 열어 방금 접수된 건을 읽기 때문이다.
      // (이 코드베이스는 보통 무효화를 기다리지 않는다. 여기만 예외다.)
      await queryClient.invalidateQueries({
        queryKey: PATHPICK_MEMBERSHIP_APPLICATIONS_QUERY_KEY(companyId),
      });
      // 신청 바디의 세금계산서 이메일은 서버가 바이어에 저장하므로,
      // 회원정보 쪽이 최신 값을 읽도록 바이어 상세도 무효화한다.
      queryClient.invalidateQueries({ queryKey: BUYER_QUERY_KEY(companyId) });
    },
    onError: (err) => {
      error(err);
    },
  });
};

export default useApplyPathPickMembership;
